/**
 * On-pace indicator, two levels: frequency compliance this SOC-anchored week and
 * ordered visits at risk in the current 30-day payment period (the LUPA view).
 * Clinical framing only — no dollars.
 */
import type { ConfigState, DateKey, Episode, PlanVisit } from "@caredna-marlin/utils/homeHealthDemo/state";
import { addDaysToKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { paymentPeriodFor, weekIndexFor } from "@caredna-marlin/utils/homeHealthDemo/engine/planExpansion";

export type WeekPace = "onPace" | "behind" | "ahead" | "quiet";
export type PeriodRisk = "safe" | "atRisk" | "belowThreshold" | "quiet";

export interface OnPace {
  weekIndex: number;
  weekStart: DateKey;
  weekEnd: DateKey;
  weekOrdered: number;
  weekDone: number;
  weekScheduled: number;
  weekMissed: number;
  weekStatus: WeekPace;
  period: number;
  periodStart: DateKey;
  periodEnd: DateKey;
  periodOrdered: number;
  periodDone: number;
  periodScheduled: number;
  periodMissed: number;
  lupaThreshold: number;
  /** Ordered visits that would need to fail before the period drops below its threshold; negative when already below. */
  cushion: number;
  visitsAtRisk: number;
  periodStatus: PeriodRisk;
}

const COUNTS_AS_ORDERED = new Set<PlanVisit["status"]>(["placeholder", "real", "completed", "missed"]);

export function selectOnPace(visits: PlanVisit[], episode: Episode, config: ConfigState, asOf: DateKey): OnPace {
  const active = visits.filter((v) => v.episodeId === episode.id && v.status !== "canceled");
  const weekIndex = Math.max(0, weekIndexFor(episode, asOf));
  const weekStart = addDaysToKey(episode.socDate, weekIndex * 7);
  const weekEnd = addDaysToKey(weekStart, 6);
  const period = Math.max(0, paymentPeriodFor(episode, config, asOf));
  const periodStart = addDaysToKey(episode.socDate, period * config.paymentPeriodDays);
  const periodEnd = addDaysToKey(periodStart, config.paymentPeriodDays - 1);

  const inWeek = active.filter((v) => v.targetDate >= weekStart && v.targetDate <= weekEnd && COUNTS_AS_ORDERED.has(v.status));
  const inPeriod = active.filter((v) => v.targetDate >= periodStart && v.targetDate <= periodEnd && COUNTS_AS_ORDERED.has(v.status));
  const done = (vs: PlanVisit[]) => vs.filter((v) => v.status === "completed").length;
  const missed = (vs: PlanVisit[]) => vs.filter((v) => v.status === "missed").length;
  const scheduled = (vs: PlanVisit[]) => vs.filter((v) => (v.status === "real" || v.status === "placeholder") && v.targetDate >= asOf).length;
  const lapsed = (vs: PlanVisit[]) => vs.filter((v) => (v.status === "real" || v.status === "placeholder") && v.targetDate < asOf).length;

  const weekOrdered = inWeek.length;
  const weekDone = done(inWeek);
  const weekScheduled = scheduled(inWeek);
  const weekMissed = missed(inWeek) + lapsed(inWeek);
  let weekStatus: WeekPace = weekMissed > 0 && weekDone + weekScheduled < weekOrdered ? "behind" : "onPace";
  if (weekDone + weekScheduled > weekOrdered) weekStatus = "ahead";

  const periodOrdered = inPeriod.length;
  const periodDone = done(inPeriod);
  const periodScheduled = scheduled(inPeriod);
  const periodMissed = missed(inPeriod) + lapsed(inPeriod);
  const threshold = episode.lupaThreshold ?? config.lupaThresholdPerPeriod;
  const expected = periodDone + periodScheduled;
  const cushion = expected - threshold;
  const visitsAtRisk = Math.max(0, threshold - expected);
  let periodStatus: PeriodRisk = cushion >= 2 ? "safe" : cushion >= 0 ? "atRisk" : "belowThreshold";

  return {
    weekIndex,
    weekStart,
    weekEnd,
    weekOrdered,
    weekDone,
    weekScheduled,
    weekMissed,
    weekStatus,
    period,
    periodStart,
    periodEnd,
    periodOrdered,
    periodDone,
    periodScheduled,
    periodMissed,
    lupaThreshold: threshold,
    cushion,
    visitsAtRisk,
    periodStatus,
  };
}

/** Compliance window for moving a visit: stays inside its SOC-anchored week (3.2.1). */
export function complianceWindowFor(visit: PlanVisit, episode: Episode): { start: DateKey; end: DateKey } {
  if (visit.rule === "recert") return { start: addDaysToKey(episode.socDate, 55), end: addDaysToKey(episode.socDate, 59) };
  const weekStart = addDaysToKey(episode.socDate, visit.weekIndex * 7);
  return { start: weekStart, end: addDaysToKey(weekStart, 6) };
}
